<script setup lang="ts">
import { $t } from '@/composables/translations/useTranslate.ts';
import ProgressBar from '../ui/ProgressBar.vue';
import ProductOffer from '../ui/ProductOffer.vue';
import BaseAccordion from '../ui/BaseAccordion.vue';
import CheckoutFooter from '../ui/CheckoutFooter.vue';
import CheckoutSummaryColumn from '../checkout/CheckoutSummaryColumn.vue';
import CheckoutPageSkeleton from '../ui/CheckoutPageSkeleton.vue';
import CheckoutContentSkeleton from '../ui/CheckoutContentSkeleton.vue';
import CheckoutInsuranceSkeleton from '../ui/CheckoutInsuranceSkeleton.vue';
import { useCheckoutStore } from '@/stores/checkout.ts';
import CheckboxList from '../ui/CheckboxList.vue';
import { ref, watch, nextTick, onMounted, computed, type ComponentPublicInstance } from 'vue';
import { useCheckoutInitializer } from '@/composables/checkout/useCheckoutInitializer';
import { useCheckoutAvailability } from '@/composables/checkout/useCheckoutAvailability';
import { useCheckoutNbc } from '@/composables/checkout/useCheckoutNbc';
import BaseAlert from '../ui/BaseAlert.vue';
import BaseErrorState from '../ui/BaseErrorState.vue';
import { useCheckoutForm } from '@/composables/checkout/useCheckoutForm';
import { useScrollToError } from '@/composables/useScrollToError';
import { useDebugMode } from '@/composables/debug/useDebugMode';
import CheckoutContactForm from '@/components/checkout/CheckoutContactForm.vue';
import CheckoutTravelerForm from '@/components/checkout/CheckoutTravelerForm.vue';
import CheckoutInvoiceRecipient from '@/components/checkout/CheckoutInvoiceRecipient.vue';
import CheckoutPaymentMethod from '@/components/checkout/CheckoutPaymentMethod.vue';
import CheckoutPaymentSchedule from '@/components/checkout/CheckoutPaymentSchedule.vue';
// Voucher block — kept next to its (still commented out) usage in the step-2 template.
// import CheckoutVoucher from '@/components/ui/CheckoutVoucher.vue';
import type { ContactFormExposed, TravelerFormExposed } from '@/types/checkout';
import { useCheckoutTerms } from '@/composables/checkout/useCheckoutTerms';
import {
  CHECKOUT_CONFIG,
  HIDE_AUXILIARIES_STEP,
  HIDE_REBATES_CHECKOUT,
} from '@/constants/checkout/config';
import { BOOKING_SUCCESS_CODES } from '@/constants/davinci/returnCodes';
import { serializePaxString } from '@/utils/parsing/paxParser';
import { deriveVariant } from '@/types/schemas/checkout/travelerVariant';
import { effectiveHotelClass } from '@/utils/checkout/classification';
import BaseHeadline from '@/components/ui/BaseHeadline.vue';
import I18nHtml from '@/components/ui/I18nHtml.vue';
import { useCheckoutErrorRecovery } from '@/composables/checkout/useCheckoutErrorRecovery';
import {
  buildCheckoutRedirectUrl,
  navigateToUrlOrBack,
} from '@/utils/checkout/checkoutRedirectHelper';
import { subscribeToNewsletter } from '@/services/api/crm';
import { buildNewsletterPayload } from '@/utils/checkout/newsletterHelper';
import { usePriceDeviation } from '@/composables/checkout/usePriceDeviation';
import { usePreSubmitValidation } from '@/composables/checkout/usePreSubmitValidation';
import { useCheckoutSsc } from '@/composables/checkout/useCheckoutSsc';
import { useCheckoutPps } from '@/composables/checkout/useCheckoutPps';
import { createIdentifierFromCheckout } from '@/utils/checkout/offerPriceStorage';
import { formatPrice } from '@/utils/formatting/currencyFormatter';
import { formatDateLocal } from '@/utils/formatting/dateFormatter';
import { useAnalytics } from '@/composables/useAnalytics';
import {
  createPurchaseEvent,
  createBookingErrorEvent,
  pushEvent,
} from '@/services/analytics/events';

const checkout = useCheckoutStore();
const { initializeCheckout, canProceed: canProceedPhase1 } = useCheckoutInitializer();
const {
  isChecking: isCheckingAvailability,
  error: availabilityError,
  isAvailable,
  hasChecked: hasCheckedAvailability,
  checkAvailability,
  retry: retryAvailability,
  getStatusMessage,
  getStatusType,
  canRetry,
} = useCheckoutAvailability();

const {
  isLoading: isLoadingNbc,
  productName,
  destinationName,
  mainImage,
  productDetailUrl,
  getRoomTypeDataByCode,
  getBestRoomImageByCode,
  fetchData: fetchNbcData,
} = useCheckoutNbc();

const { isDebugMode } = useDebugMode();

const {
  storedPrices: priceDeviationStoredPrices,
  priceDeviationResult,
  hasAnyDeviation: hasPriceDeviation,
  initialize: initializePriceDeviation,
} = usePriceDeviation();

const {
  isLoading: isLoadingErrorRecovery,
  canBuildPdpUrl,
  fetchPdpUrl,
  navigateBack,
} = useCheckoutErrorRecovery();

const analytics = useAnalytics();
const preSubmitValidation = usePreSubmitValidation();

const {
  isLoading: isLoadingSsc,
  hasData: hasSscData,
  insuranceDisplayItems,
  selectedInsuranceId,
  selectedInsurancePrice,
  fetchData: fetchSscData,
  selectInsurance,
} = useCheckoutSsc();

const {
  isLoading: isLoadingPps,
  hasData: hasPpsData,
  extrasGroups,
  selectedExtrasPrice,
  fetchData: fetchPpsData,
  selectServiceType,
  getSelectedServiceTypeCode,
  selectPairedServiceType,
  getSelectedPairedServiceTypeCode,
} = useCheckoutPps();

const addressCorrectionAlertRef = ref<HTMLElement | null>(null);

interface DebugBookingResult {
  success: boolean;
  redirectUrl: string;
  bookingNumber?: string;
  returnCode: number;
  message?: string;
  error?: string;
}

const debugBookingResult = ref<DebugBookingResult | null>(null);

const progressBarRef = ref<HTMLElement | null>(null);

// Initialization state
const isInitializing = ref(true);
const validationErrorMessages = ref<string[]>([]);

// Form Refs (for component-based forms)
const contactFormRef = ref<ContactFormExposed | null>(null);
const travelerFormRefs = ref<Map<number, TravelerFormExposed | null>>(new Map());

function setTravelerFormRef(index: number, el: TravelerFormExposed | null): void {
  if (el) {
    travelerFormRefs.value.set(index, el);
  } else {
    travelerFormRefs.value.delete(index);
  }
}

const checkoutForm = useCheckoutForm();

// Scroll to first error helper
const { scrollToFirstError } = useScrollToError();

// Terms checkboxes (statutory + contractual + privacy + newsletter)
const terms = useCheckoutTerms();

// Initial full-page loading state (init → availability → NBC enrichment).
// Booking submission and pre-submit validation are intentionally excluded:
// the former shows a blocking overlay over the already-rendered checkout,
// the latter only spins the footer CTA so the filled form stays visible.
//
// `isLoadingAgeBands` is intentionally part of this gate: each
// CheckoutTravelerForm freezes its Zod schema from `ageBands` at mount, so the band MUST
// resolve before the forms render — otherwise a form would freeze on FALLBACK_BANDS and a
// fallback-edge child could pass the UI but fail booking with 670. The probe runs in
// parallel with BA#1 (one extra request) and is bounded by the DaVinci client timeout, so
// it does not extend the wait in the common case.
const isInitialLoading = computed(
  () =>
    isInitializing.value ||
    isCheckingAvailability.value ||
    checkout.isLoadingAgeBands ||
    isLoadingNbc.value
);

const passengerVariants = computed(
  () => new Map((checkout.passengers ?? []).map((p, i) => [i, deriveVariant(p)]))
);

function formatPassengersLabel(
  passengers: Array<{ type: string; age?: number; hotelClass?: 'adult' | 'child' | 'infant' }>
): string {
  // Prefer the hotel's classification; plain/bookmarked URLs fall back to the binary type.
  const adults = passengers.filter((p) => effectiveHotelClass(p) === 'adult').length;
  const children = passengers.filter((p) => effectiveHotelClass(p) === 'child').length;
  const infants = passengers.filter((p) => effectiveHotelClass(p) === 'infant').length;

  const parts = [];
  if (adults > 0) {
    const adultLabel =
      adults === 1
        ? $t('ibe.checkout.passengers.adult-singular', 'Adult')
        : $t('ibe.checkout.passengers.adult-plural', 'Adults');
    parts.push(`${adults} ${adultLabel}`);
  }
  if (children > 0) {
    const childLabel =
      children === 1
        ? $t('ibe.checkout.passengers.child-singular', 'Child')
        : $t('ibe.checkout.passengers.child-plural', 'Children');
    parts.push(`${children} ${childLabel}`);
  }
  if (infants > 0) {
    const infantLabel =
      infants === 1
        ? $t('ibe.checkout.passengers.infant-singular', 'Infant')
        : $t('ibe.checkout.passengers.infant-plural', 'Infants');
    parts.push(`${infants} ${infantLabel}`);
  }

  return parts.join(', ');
}

const roomsData = computed(() => {
  if (!checkout.roomCombination) return [];

  return checkout.roomCombination.products.map((product, arrayIndex) => {
    const roomType = getRoomTypeDataByCode(product.roomCode);
    const serviceIndex = arrayIndex + 1; // DaVinci Service.Index is 1-based

    const allSeparateItems = checkout.separateItems.get(serviceIndex) ?? [];
    const hiddenRebate = HIDE_REBATES_CHECKOUT
      ? allSeparateItems.filter((item) => item.isRebate).reduce((sum, item) => sum + item.amount, 0)
      : 0;
    const roomPrice = checkout.roomOnlyPrices.get(serviceIndex) ?? product.price ?? 0;

    return {
      roomTypeName: roomType?.roomTypeName || product.roomDescription || product.roomCode,
      passengersLabel: formatPassengersLabel(product.passengers),
      price: Math.round((roomPrice + hiddenRebate) * 100) / 100,
      boardName: checkout.boardNames.get(serviceIndex),
      boardDescription: checkout.boardDescriptions.get(serviceIndex) ?? null,
      separateItems: HIDE_REBATES_CHECKOUT
        ? allSeparateItems.filter((item) => !item.isRebate)
        : allSeparateItems,
      roomCode: product.roomCode,
      images: roomType?.roomTypeImages ?? [],
      description: roomType?.roomTypeDescription ?? null,
    };
  });
});

const baseTotalPrice = computed(() => {
  return checkout.accommodationTotal;
});

const totalPrice = computed(() => {
  return baseTotalPrice.value + selectedInsurancePrice.value + selectedExtrasPrice.value;
});

const hotelImageUrl = computed(() => {
  if (mainImage.value) {
    return mainImage.value;
  }

  if (checkout.roomCodes && checkout.roomCodes.length > 0) {
    return getBestRoomImageByCode(checkout.roomCodes[0]) || undefined;
  }

  return undefined;
});

const popupHints = computed<string[] | undefined>(() => {
  if (checkout.popupHints && checkout.popupHints.length > 0) {
    return [...checkout.popupHints];
  }
  return undefined;
});

const checkoutButtonText = computed(() => {
  if (checkout.activeStep === 0) {
    return checkout.activeSubStep === 0
      ? $t('ibe.checkout.button.next', 'Next')
      : $t('ibe.checkout.button.to-data-input', 'To data entry');
  }
  return checkout.activeStep === 1
    ? $t('ibe.checkout.button.to-overview', 'To overview')
    : checkout.isRequestSubmitCta
      ? $t('ibe.progress.bookrequest.cta', 'Submit request')
      : $t('ibe.checkout.button.book-now', 'Book now and pay');
});

// Computed: Services data for sidebar summary (extras + insurance selection)
const servicesData = computed(() => {
  const items: Array<{ name: string; nameLabel?: string; price?: number; included: boolean }> = [];

  // Extras section
  if (hasPpsData.value) {
    for (const group of extrasGroups.value) {
      const selectedStCode =
        group.kind === 'single'
          ? getSelectedServiceTypeCode(group.id)
          : getSelectedPairedServiceTypeCode(group.inboundId, group.outboundId);
      if (!selectedStCode) continue;
      const selectedSt = group.serviceTypes.find((st) => st.id === selectedStCode);
      if (selectedSt) {
        items.push({
          name: selectedSt.title,
          nameLabel: group.title,
          price: selectedSt.priceRaw,
          included: true,
        });
      }
    }
  }

  // Insurance section
  if (hasSscData.value) {
    const selected = insuranceDisplayItems.value.find(
      (item) => item.id === selectedInsuranceId.value
    );
    if (selected) {
      items.push({
        name: selected.title,
        price: selected.priceRaw,
        included: true,
      });
    } else {
      items.push({
        name: $t('ibe.checkout.ssc.no-insurance-selected', 'No insurance selected'),
        included: false,
      });
    }
  }

  return items;
});

const servicesTotalPrice = computed(() => {
  return selectedInsurancePrice.value + selectedExtrasPrice.value;
});

// Collapsed mobile summary card data
const paxLabel = computed(() =>
  formatPassengersLabel((checkout.roomCombination?.products ?? []).flatMap((p) => p.passengers))
);

const dateRange = computed(() => {
  const from = checkout.checkInFormatted;
  const to = checkout.checkOutFormatted;
  if (from && to) return `${from} – ${to}`;
  return from || to || '';
});

// Single bundle of props shared by both summary mounts (mobile + desktop)
const summaryProps = computed(() => ({
  dateRange: dateRange.value,
  paxLabel: paxLabel.value,
  hotelName: productName.value || checkout.productCode || 'Hotel',
  hotelLocation: destinationName.value || '',
  hotelImageUrl: hotelImageUrl.value,
  organizerName: checkout.agencyName || undefined,
  travelType: checkout.travelType || undefined,
  notes: popupHints.value,
  checkIn: checkout.checkInFormatted,
  checkOut: checkout.checkOutFormatted,
  nights: checkout.nightCount,
  rooms: roomsData.value,
  auxServices: checkout.auxServices,
  totalPrice: baseTotalPrice.value,
  hasPriceDeviation: hasPriceDeviation.value,
  priceDeviationAmount: priceDeviationResult.value?.totalDeviation,
  originalTotalPrice: priceDeviationResult.value?.originalTotalPrice,
  hasDiscount: checkout.hasDiscount,
  discountAmount: checkout.totalDiscount,
  discountPercent: checkout.discountPercent,
  discountReasons: checkout.discountReasons,
  discountOriginalPrice: checkout.originalPrice,
  services: servicesData.value,
  servicesTotalPrice: servicesTotalPrice.value > 0 ? servicesTotalPrice.value : undefined,
  servicesNote: !selectedInsuranceId.value
    ? $t(
        'ibe.checkout.ssc.no-insurance-note',
        'I deliberately waive the conclusion of the recommended travel insurance products. Vamos Reisen expressly recommends travel insurance.'
      )
    : undefined,
  showServicesEdit: !HIDE_AUXILIARIES_STEP,
  travelers: checkout.travelersSummary,
  activeStep: checkout.activeStep,
}));

const mobileSummaryRef = ref<InstanceType<typeof CheckoutSummaryColumn> | null>(null);
const desktopSummaryRef = ref<InstanceType<typeof CheckoutSummaryColumn> | null>(null);

/**
 * "All details" in the footer card scrolls to the summary. Only one of the two
 * mounts is visible at a time, and the mobile one has to be expanded first.
 */
function handleShowDetails() {
  const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
  const target = isDesktop ? desktopSummaryRef.value : mobileSummaryRef.value;
  if (!target) return;
  if (!isDesktop) target.expand();
  nextTick(() => {
    (target.$el as HTMLElement | null)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

function handleChangeRoomSelection() {
  const url = buildCheckoutRedirectUrl({
    pdpUrl: productDetailUrl.value ?? '',
    checkIn: checkout.checkIn,
    checkOut: checkout.checkOut,
    nightCount: checkout.nightCount,
    roomOccupancy: checkout.roomOccupancy,
  });

  navigateToUrlOrBack(url);
}

async function handleNextStep() {
  // Step 1 → Step 2: Validate contact and traveler forms
  if (checkout.activeStep === 1) {
    // 1. Validate forms locally first
    const success = await checkoutForm.saveToStore(contactFormRef.value, travelerFormRefs.value);

    if (!success) {
      validationErrorMessages.value = [
        $t('ibe.checkout.error.validation-failed', 'Please correct the errors in the form'),
      ];

      await scrollToFirstError();
      return;
    }

    validationErrorMessages.value = [];

    // 2. Perform contact data BA check to validate address
    const formData = checkout.travelerData;
    if (!formData) {
      return;
    }

    const canProceed = await preSubmitValidation.validate(formData);

    if (!canProceed) {
      // Recoverable: address corrections, or selected auxiliaries became unavailable
      if (preSubmitValidation.shouldStayOnForm.value) {
        if (preSubmitValidation.hasAddressChanges.value) {
          preSubmitValidation.applyAddressCorrections(contactFormRef.value);
        }

        await nextTick();
        (addressCorrectionAlertRef.value as ComponentPublicInstance | null)?.$el?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        return;
      }

      if (!isDebugMode.value) {
        window.location.href = CHECKOUT_CONFIG.REDIRECT_URLS.DEAD_END;
      }
      return;
    }

    preSubmitValidation.clearValidation();
  }

  // Step 2 → Submit Booking: Validate terms and submit to API
  if (checkout.activeStep === 2) {
    terms.markSubmitAttempted();

    const termsResult = await terms.validate();

    if (!termsResult.valid) {
      validationErrorMessages.value = [
        $t('ibe.checkout.error.terms-required', 'Please accept the required terms and conditions'),
      ];
      await scrollToFirstError();
      return;
    }

    validationErrorMessages.value = [];

    const result = await checkout.submitBooking();

    // Flow change: API switched the booking type
    if (result.flowChanged && result.newAvailabilityCode !== undefined) {
      checkout.applyBookingFlowChange(result.newAvailabilityCode);
      return;
    }

    if (result.success && result.bookingNumber) {
      // Newsletter subscription (silent - does not block redirect)
      if (
        terms.values.newsletterOptIn &&
        checkout.travelerData?.contactPerson &&
        checkout.passengers &&
        checkout.productCode
      ) {
        const newsletterPayload = buildNewsletterPayload(
          checkout.travelerData.contactPerson,
          checkout.passengers,
          checkout.productCode,
          checkout.travelerData.invoiceRecipient === 'solo'
        );

        if (newsletterPayload) {
          const subscriptionResult = await subscribeToNewsletter(newsletterPayload);

          if (!subscriptionResult.success) {
            console.warn('[Checkout] Newsletter subscription failed:', subscriptionResult.error);
          }
        }
      }

      const shareParams = new URLSearchParams();
      shareParams.set('bookingNumber', result.bookingNumber);

      if (checkout.productCode) {
        shareParams.set('productCode', checkout.productCode);
      }
      if (checkout.checkIn) {
        shareParams.set('checkIn', formatDateLocal(checkout.checkIn));
      }
      if (checkout.checkOut) {
        shareParams.set('checkOut', formatDateLocal(checkout.checkOut));
      }
      if (checkout.nightCount > 0) {
        shareParams.set('nights', checkout.nightCount.toString());
      }
      if (checkout.roomOccupancy) {
        const paxString = serializePaxString(checkout.roomOccupancy);
        if (paxString) {
          shareParams.set('pax', paxString);
        }
      }

      const isRequestOutcome =
        checkout.requiresRequestBooking ||
        BOOKING_SUCCESS_CODES.REQUEST_BOOKING.includes(result.returnCode);
      const baseUrl = isRequestOutcome
        ? CHECKOUT_CONFIG.REDIRECT_URLS.REQUEST_THANK_YOU
        : CHECKOUT_CONFIG.REDIRECT_URLS.THANK_YOU;

      const redirectUrl = `${baseUrl}?${shareParams.toString()}`;

      if (isDebugMode.value) {
        debugBookingResult.value = {
          success: true,
          redirectUrl,
          bookingNumber: result.bookingNumber,
          returnCode: result.returnCode,
          message: result.message,
        };
        console.log('[Checkout] DEBUG MODE - Success result:', debugBookingResult.value);
        analytics.trackAddPaymentInfo();
        pushEvent(
          createPurchaseEvent({
            session: checkout.sessionId,
            currency: 'EUR',
            value: checkout.totalBookingPrice,
            params: analytics.buildCheckoutParameters(),
            bookingWarning: checkout.requiresRequestBooking ? 'request_booking' : undefined,
            transactionId: result.bookingNumber,
            items: [analytics.buildCheckoutItem()],
          })
        );
      } else {
        analytics.trackPurchaseBeforeRedirect(result.bookingNumber, () => {
          window.location.href = redirectUrl;
        });
      }
    } else {
      if (isDebugMode.value) {
        debugBookingResult.value = {
          success: false,
          redirectUrl: CHECKOUT_CONFIG.REDIRECT_URLS.DEAD_END,
          returnCode: result.returnCode,
          message: result.message,
          error: result.error,
        };
        console.log('[Checkout] DEBUG MODE - Error result:', debugBookingResult.value);
        analytics.trackAddPaymentInfo();
        pushEvent(
          createBookingErrorEvent({
            session: checkout.sessionId,
            davinciCode: result.returnCode,
            errorText: result.error || result.message || 'Unknown booking error',
            params: analytics.buildCheckoutParameters(),
          })
        );
      } else {
        analytics.trackBookingErrorBeforeRedirect(
          result.returnCode,
          result.error || result.message || 'Unknown booking error',
          () => {
            window.location.href = CHECKOUT_CONFIG.REDIRECT_URLS.DEAD_END;
          }
        );
      }
    }
    return;
  }

  // Step 0: Navigate between sub-steps before advancing to step 1
  if (checkout.activeStep === 0 && checkout.activeSubStep === 0) {
    checkout.setActiveSubStep(1);
    fetchSscData();
    return;
  }

  checkout.setActiveStep(checkout.activeStep + 1);
}

watch([() => checkout.activeStep, () => checkout.activeSubStep], async () => {
  await nextTick();
  progressBarRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  // Track step transitions
  if (checkout.activeStep === 0 && checkout.activeSubStep === 1) {
    analytics.trackCheckoutInsuranceStep();
  } else if (checkout.activeStep === 1) {
    analytics.trackCheckoutDataStep();
  } else if (checkout.activeStep === 2) {
    analytics.trackCheckoutPaymentStep();
  }
});

// Auto-skip PPS substep when there are no extras for this booking.
watch([() => checkout.activeStep, () => checkout.activeSubStep, isLoadingPps], async () => {
  if (
    checkout.activeStep === 0 &&
    checkout.activeSubStep === 0 &&
    !isLoadingPps.value &&
    !hasPpsData.value
  ) {
    checkout.setActiveSubStep(1);
    if (!hasSscData.value && !isLoadingSsc.value) {
      await fetchSscData();
    }
  }
});

onMounted(async () => {
  // Phase 1: Parse and validate URL parameters
  const { isValid, errors } = await initializeCheckout();

  if (!isValid) {
    validationErrorMessages.value = errors;
    isInitializing.value = false;

    // If productCode is available, fetch NBC for PDP URL
    if (canBuildPdpUrl.value) {
      await fetchPdpUrl();
    }
    return;
  }

  isInitializing.value = false;

  // Phase 2: Check availability + fire the age-band probe in parallel (the probe is
  // independent of BA #1's session, so total latency is max(), not the sum).
  if (canProceedPhase1()) {
    await Promise.all([checkAvailability(), checkout.fetchAgeBands()]);

    // Track checkout BA result
    if (checkout.hasCheckedAvailability) {
      if (checkout.isAvailable) {
        analytics.trackBookingCheckSuccess(
          checkout.availabilityCode!,
          checkout.sessionId,
          'checkout'
        );
        analytics.trackCheckoutEntered();
      } else {
        analytics.trackBookingCheckFail(
          checkout.availabilityCode ?? 999,
          checkout.availabilityError ?? 'Unavailable',
          checkout.sessionId,
          'checkout'
        );
      }
    }
  }

  // Initialize price deviation detection (after availability check)
  if (isAvailable.value && checkout.roomCombination) {
    const paxString = checkout.roomOccupancy ? serializePaxString(checkout.roomOccupancy) : '';

    const identifier = createIdentifierFromCheckout(
      checkout.productCode || '',
      checkout.offerIds?.join('-') || '',
      checkout.roomCodes?.join('-') || '',
      // ?? (not ||) so an intentional no-meal boardCode ('') is preserved, only null → ''
      checkout.boardCode ?? '',
      // Local calendar day — must match the offer side's identifier, which uses the
      // Player date string; toISOString() would shift a day for CET/CEST and the
      // identifiers would never match (silently disabling deviation detection).
      checkout.checkIn ? formatDateLocal(checkout.checkIn) : '',
      checkout.checkOut ? formatDateLocal(checkout.checkOut) : '',
      paxString || ''
    );

    initializePriceDeviation({
      identifier,
      totalPrice: checkout.roomCombination.totalPrice,
    });
  }

  // Phase 2.5 + Phase 3: Fetch PPS, SSC, and NBC in parallel (all optional)
  if (isAvailable.value) {
    await Promise.all(HIDE_AUXILIARIES_STEP ? [fetchNbcData()] : [fetchNbcData(), fetchPpsData()]);
  }
});
</script>

<template>
  <div class="pt-10">
    <div v-if="isDebugMode && debugBookingResult" class="mb-8 wrapper">
      <div
        :class="[
          'p-6 rounded-lg border-2',
          debugBookingResult.success ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500',
        ]"
      >
        <h2
          :class="[
            'text-xl font-bold mb-4',
            debugBookingResult.success ? 'text-green-700' : 'text-red-700',
          ]"
        >
          {{ debugBookingResult.success ? '✅ Booking Successful' : '❌ Booking Failed' }}
        </h2>

        <div class="space-y-3 text-sm">
          <div class="flex gap-2">
            <span class="font-semibold text-gray-700">Return Code:</span>
            <code class="px-2 py-0.5 bg-gray-100 rounded font-mono">{{
              debugBookingResult.returnCode
            }}</code>
          </div>

          <div v-if="debugBookingResult.bookingNumber" class="flex gap-2">
            <span class="font-semibold text-gray-700">Booking Number:</span>
            <code class="px-2 py-0.5 bg-green-100 rounded font-mono text-green-800">{{
              debugBookingResult.bookingNumber
            }}</code>
          </div>

          <div v-if="debugBookingResult.message" class="flex flex-col gap-1">
            <span class="font-semibold text-gray-700">Message:</span>
            <p class="p-2 bg-gray-100 rounded text-gray-800">{{ debugBookingResult.message }}</p>
          </div>

          <div v-if="debugBookingResult.error" class="flex flex-col gap-1">
            <span class="font-semibold text-red-700">Error:</span>
            <p class="p-2 bg-red-100 rounded text-red-800">{{ debugBookingResult.error }}</p>
          </div>

          <div class="flex flex-col gap-1 pt-2 border-t border-gray-200">
            <span class="font-semibold text-gray-700">Would redirect to:</span>
            <code class="p-2 bg-gray-100 rounded font-mono text-xs break-all">{{
              debugBookingResult.redirectUrl
            }}</code>
          </div>

          <div
            v-if="checkout.bookingError"
            class="flex flex-col gap-1 pt-2 border-t border-gray-200"
          >
            <span class="font-semibold text-red-700">Store Booking Error:</span>
            <p class="p-2 bg-red-100 rounded text-red-800">{{ checkout.bookingError }}</p>
          </div>

          <div
            v-if="checkout.bookingResult"
            class="flex flex-col gap-1 pt-2 border-t border-gray-200"
          >
            <span class="font-semibold text-gray-700">Full Booking Result:</span>
            <pre class="p-2 bg-gray-100 rounded text-xs overflow-x-auto">{{
              JSON.stringify(checkout.bookingResult, null, 2)
            }}</pre>
          </div>
        </div>

        <div class="mt-4 pt-4 border-t border-gray-200">
          <button
            class="btn-secondary px-4 py-2 text-sm"
            @click="
              debugBookingResult = null;
              checkout.setActiveStep(2);
            "
          >
            Clear Result & Try Again
          </button>
        </div>
      </div>
    </div>

    <!-- Booking submission shows a spinner on the footer CTA only (no full-page block),
         the same way pre-submit validation does in the form step. -->
    <CheckoutPageSkeleton v-if="isInitialLoading" />

    <!-- Error State: Missing Parameters (Phase 1) -->
    <div v-else-if="!canProceedPhase1()" class="py-8 wrapper">
      <BaseErrorState
        icon="icon-warning"
        icon-class="bg-error"
        :title="$t('ibe.checkout.error.title', 'Missing Checkout Information')"
        :message="
          $t(
            'ibe.checkout.error.message',
            'The checkout could not be loaded because required information is missing. Please go back and try again.'
          )
        "
        :primary-button="{
          text: $t('ibe.checkout.error.button-back', 'Go Back'),
          onClick: navigateBack,
          disabled: isLoadingErrorRecovery,
          loading: isLoadingErrorRecovery,
        }"
        size="default"
      />

      <!-- Show specific validation errors -->
      <div v-if="validationErrorMessages.length > 0" class="max-w-2xl mx-auto mt-8">
        <BaseAlert
          v-for="(error, index) in validationErrorMessages.slice(0, 3)"
          :key="index"
          type="error"
          :message="error"
          class="mb-2"
        />
        <p v-if="validationErrorMessages.length > 3" class="mt-2 text-sm text-center text-gray-600">
          {{
            $t('ibe.checkout.error.more-errors', 'And {count} more errors...', {
              count: validationErrorMessages.length - 3,
            })
          }}
        </p>
      </div>
    </div>

    <!-- Error State: Unavailable Offer (Phase 2) -->
    <div v-else-if="hasCheckedAvailability && !isAvailable" class="py-8 wrapper">
      <BaseErrorState
        icon="icon-warning"
        icon-class="bg-error"
        :title="$t('ibe.checkout.availability.unavailable.title', 'Offer Not Available')"
        :message="
          isDebugMode
            ? availabilityError ||
              $t(
                'ibe.checkout.availability.unavailable.message',
                'Unfortunately, this offer is no longer available. Please select a different offer or try again later.'
              )
            : $t(
                'ibe.availability.generic-error',
                'We apologize, but this room type is already fully booked for the selected dates. Please select a different room or a different period.'
              )
        "
        :primary-button="{
          text: $t('ibe.checkout.availability.button.return', 'Return to Offers'),
          onClick: navigateBack,
        }"
        :secondary-button="
          canRetry()
            ? {
                text: $t('ibe.checkout.availability.button.retry', 'Try Again'),
                onClick: retryAvailability,
              }
            : undefined
        "
        size="compact"
      />
    </div>

    <!-- Normal Checkout Flow -->
    <div v-else class="wrapper">
      <div v-if="hasCheckedAvailability && isAvailable && isDebugMode" class="mb-8">
        <BaseAlert :type="getStatusType()" :message="getStatusMessage()" />

        <!-- Provider Debug Info -->
        <div class="mt-4 p-4 bg-gray-100 rounded-lg border border-gray-300">
          <h3 class="font-semibold text-gray-700 mb-2">Provider Information (Debug)</h3>
          <div class="space-y-1 text-sm">
            <div class="flex gap-2">
              <span class="font-medium text-gray-600">TravelType (BA response):</span>
              <code class="px-2 py-0.5 bg-white rounded">{{
                checkout.travelType === null ? 'null' : checkout.travelType || '(empty)'
              }}</code>
            </div>
            <div class="flex gap-2">
              <span class="font-medium text-gray-600">AgencyNumber (derived):</span>
              <code class="px-2 py-0.5 bg-white rounded">{{
                checkout.agencyNumber ?? 'null'
              }}</code>
            </div>
            <div v-if="checkout.agencyNumberIsFallback" class="flex gap-2">
              <span class="font-medium text-orange-600">AgencyNumber fallback:</span>
              <span class="text-orange-600">
                TravelType prefix is neither VR nor VV — defaulted to 1
              </span>
            </div>
            <div class="flex gap-2">
              <span class="font-medium text-gray-600">Tour Operator:</span>
              <code class="px-2 py-0.5 bg-white rounded">{{
                checkout.tourOperator || 'null'
              }}</code>
            </div>
            <div class="flex gap-2">
              <span class="font-medium text-gray-600">Is Vamos Provider:</span>
              <span :class="checkout.isVamosTourOperator ? 'text-green-600' : 'text-orange-600'">
                {{ checkout.isVamosTourOperator ? 'Yes (Vamos)' : 'No (Third-party)' }}
              </span>
            </div>
            <div class="flex gap-2">
              <span class="font-medium text-gray-600">Agency Name:</span>
              <code class="px-2 py-0.5 bg-white rounded">{{ checkout.agencyName || 'null' }}</code>
            </div>
            <div class="flex gap-2">
              <span class="font-medium text-gray-600">Provider Terms Link (NBC):</span>
              <code
                class="px-2 py-0.5 rounded break-all"
                :class="
                  checkout.providerTermsLink
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                "
              >
                {{ checkout.providerTermsLink || 'null' }}
              </code>
            </div>
            <div class="flex gap-2">
              <span class="font-medium text-gray-600">Cancellation Terms (NBC):</span>
              <code
                class="px-2 py-0.5 rounded break-all"
                :class="
                  checkout.cancellationTermsLink
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                "
              >
                {{ checkout.cancellationTermsLink || 'null' }}
              </code>
            </div>
            <div class="flex gap-2">
              <span class="font-medium text-gray-600">isPackage (BA PRK):</span>
              <code
                class="px-2 py-0.5 rounded break-all"
                :class="checkout.isPackage ? 'bg-green-100 text-green-800' : 'bg-gray-100'"
              >
                {{ checkout.isPackage }}
              </code>
            </div>
            <div class="flex gap-2">
              <span class="font-medium text-gray-600">VVI Url (BA ExternalAttachments):</span>
              <code
                class="px-2 py-0.5 rounded break-all"
                :class="checkout.vviUrl ? 'bg-green-100 text-green-800' : 'bg-gray-100'"
              >
                {{ checkout.vviUrl || 'null' }}
              </code>
            </div>
          </div>
        </div>

        <!-- Price Deviation Debug Info -->
        <div class="mt-4 p-4 bg-gray-100 rounded-lg border border-gray-300">
          <h3 class="font-semibold text-gray-700 mb-2">Price Deviation Detection (Debug)</h3>
          <div class="space-y-1 text-sm">
            <div class="flex gap-2">
              <span class="font-medium text-gray-600">Stored Prices Loaded:</span>
              <span :class="priceDeviationStoredPrices ? 'text-green-600' : 'text-orange-600'">
                {{ priceDeviationStoredPrices ? 'Yes' : 'No' }}
              </span>
            </div>
            <div class="flex gap-2">
              <span class="font-medium text-gray-600">Has Price Deviation:</span>
              <span :class="hasPriceDeviation ? 'text-red-600 font-bold' : 'text-green-600'">
                {{ hasPriceDeviation ? 'YES - PRICE CHANGED!' : 'No' }}
              </span>
            </div>
            <template v-if="priceDeviationResult">
              <div class="flex gap-2">
                <span class="font-medium text-gray-600">Original Total (Player API):</span>
                <code class="px-2 py-0.5 bg-white rounded">
                  {{ formatPrice(priceDeviationResult.originalTotalPrice, true) }}
                </code>
              </div>
              <div class="flex gap-2">
                <span class="font-medium text-gray-600">Current Total (Checkout):</span>
                <code class="px-2 py-0.5 bg-white rounded">
                  {{ formatPrice(priceDeviationResult.currentTotalPrice, true) }}
                </code>
              </div>
              <div class="flex gap-2">
                <span class="font-medium text-gray-600">Total Deviation:</span>
                <code
                  class="px-2 py-0.5 rounded"
                  :class="
                    priceDeviationResult.totalDeviation > 0
                      ? 'bg-red-100 text-red-800'
                      : priceDeviationResult.totalDeviation < 0
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100'
                  "
                >
                  {{ priceDeviationResult.totalDeviation > 0 ? '+' : ''
                  }}{{ formatPrice(priceDeviationResult.totalDeviation, true) }}
                </code>
              </div>
            </template>
            <div v-else class="text-gray-500 italic">
              No stored prices found (localStorage empty or identifier mismatch)
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col justify-center gap-10 lg:flex-row">
        <div class="flex flex-col items-center pb-5 lg:w-2/3">
          <div
            v-if="checkout.activeStep < 3"
            ref="progressBarRef"
            class="flex justify-center w-full"
          >
            <ProgressBar />
          </div>

          <!-- Mobile: collapsed summary above the step content -->
          <CheckoutSummaryColumn
            v-if="checkout.activeStep < 3"
            ref="mobileSummaryRef"
            v-bind="summaryProps"
            collapsible
            class="lg:hidden w-full mb-6"
            @edit-room="handleChangeRoomSelection"
            @edit-services="() => checkout.setActiveStep(0)"
            @edit-contact="() => checkout.setActiveStep(1)"
          />

          <div class="relative w-full overflow-hidden -mx-2">
            <div :key="`${checkout.activeStep}-${checkout.activeSubStep}`" class="w-full px-2">
              <template v-if="checkout.activeStep === 0">
                <!-- Sub-step 0: Hotel extras (PPS data) -->
                <template v-if="checkout.activeSubStep === 0">
                  <!-- Loading state -->
                  <CheckoutContentSkeleton v-if="isLoadingPps" class="mb-7.5" />

                  <!-- Empty / error state -->
                  <div
                    v-else-if="!hasPpsData"
                    class="rounded-xl px-2.5 py-3.5 bg-highlight-2 flex items-center gap-2.5 mb-7.5"
                  >
                    <i class="rotate-180 bg-black h-9 w-15 icon icon-info"></i>
                    <p class="text-body">
                      {{
                        $t(
                          'ibe.checkout.pps.no-extras',
                          'No additional extras available for this booking.'
                        )
                      }}
                    </p>
                  </div>

                  <!-- Real extras from PPS — one accordion per service group.
                       Transport inbound+outbound are merged into a single 'pair' group
                       so one click selects both legs. -->
                  <template v-else>
                    <BaseAccordion
                      v-for="group in extrasGroups"
                      :key="`extra-group-${group.id}`"
                      :title="group.title"
                      :has-border="false"
                      :default-open="false"
                    >
                      <template v-if="group.kind === 'single'">
                        <ProductOffer
                          v-for="(st, index) in group.serviceTypes"
                          :key="`st-${group.id}-${st.id}`"
                          :title="st.title"
                          :description="''"
                          :availability="st.availability"
                          :price="st.price"
                          :wrapper-class="st.wrapperClass"
                          :under-accordion="true"
                          :is-selected="getSelectedServiceTypeCode(group.id) === st.id"
                          :is-last="index === group.serviceTypes.length - 1"
                          @cta-click="selectServiceType(group.id, st.id)"
                        />
                      </template>
                      <template v-else>
                        <ProductOffer
                          v-for="(st, index) in group.serviceTypes"
                          :key="`st-pair-${group.inboundId}-${group.outboundId}-${st.id}`"
                          :title="st.title"
                          :description="''"
                          :availability="st.availability"
                          :price="st.price"
                          :wrapper-class="st.wrapperClass"
                          :under-accordion="true"
                          :is-selected="
                            getSelectedPairedServiceTypeCode(group.inboundId, group.outboundId) ===
                            st.id
                          "
                          :is-last="index === group.serviceTypes.length - 1"
                          @cta-click="
                            selectPairedServiceType(group.inboundId, group.outboundId, st.id)
                          "
                        />
                      </template>
                    </BaseAccordion>
                  </template>
                </template>

                <!-- Sub-step 1: Insurance (SSC data) -->
                <template v-else>
                  <BaseAccordion
                    :title="$t('ibe.accordion.title-1')"
                    :default-open="true"
                    :has-border="false"
                  >
                    <!-- Loading state -->
                    <CheckoutInsuranceSkeleton v-if="isLoadingSsc" />

                    <!-- Empty / error state -->
                    <div v-else-if="!hasSscData" class="py-4">
                      <p class="text-body text-text-primary">
                        {{
                          $t(
                            'ibe.checkout.ssc.no-offers',
                            'No insurance options available for this booking.'
                          )
                        }}
                      </p>
                    </div>

                    <!-- Real insurance offers from SSC -->
                    <template v-else>
                      <ProductOffer
                        v-for="offer in insuranceDisplayItems"
                        :key="`insurance-${offer.id}`"
                        :title="offer.title"
                        :description="offer.description"
                        :availability="offer.availability"
                        :price="offer.price"
                        :wrapper-class="offer.wrapperClass"
                        :under-accordion="offer.underAccordion"
                        :is-selected="selectedInsuranceId === offer.id"
                        :is-last="!offer.wrapperClass"
                        @cta-click="selectInsurance(offer.id)"
                      />
                    </template>
                  </BaseAccordion>
                </template>
              </template>

              <template v-else-if="checkout.activeStep === 1">
                <CheckoutContactForm
                  ref="contactFormRef"
                  :initial-values="checkoutForm.getInitialContactValues()"
                  :is-traveling="checkoutForm.invoiceRecipient.value === 'solo'"
                />

                <CheckoutInvoiceRecipient v-model="checkoutForm.invoiceRecipient.value" />

                <template
                  v-for="group in checkoutForm.travelersByRoom.value"
                  :key="`room-${group.roomNumber}`"
                >
                  <h3 v-if="checkout.isMultiRoom" class="h3-styling mb-6 text-text-primary">
                    {{
                      $t('ibe.checkout.room-heading', 'Room {number}', {
                        number: group.roomNumber,
                      })
                    }}
                  </h3>

                  <CheckoutTravelerForm
                    v-for="travelerIndex in group.indices"
                    :key="travelerIndex"
                    :ref="
                      (el) => setTravelerFormRef(travelerIndex, el as TravelerFormExposed | null)
                    "
                    :traveler-id="travelerIndex"
                    :traveler-variant="passengerVariants.get(travelerIndex)!"
                    :age-bands="checkout.ageBands"
                    :check-in-date="checkout.checkIn!"
                    :is-classified="checkout.passengers?.[travelerIndex]?.hotelClass !== undefined"
                    :traveler-number="
                      checkoutForm.getTravelerDisplayNumber(
                        travelerIndex,
                        passengerVariants.get(travelerIndex)!
                      )
                    "
                    :initial-values="checkoutForm.getInitialTravelerValues(travelerIndex)"
                  />
                </template>

                <!-- Address Correction Alert (from pre-submit BA check) -->
                <BaseAlert
                  v-if="preSubmitValidation.addressCorrectionAlertData.value"
                  ref="addressCorrectionAlertRef"
                  type="warning"
                  class="mt-4 mb-6"
                >
                  <!-- Field changes summary -->
                  <p
                    v-if="
                      preSubmitValidation.addressCorrectionAlertData.value.addressChanges.length > 0
                    "
                    class="font-medium"
                  >
                    {{
                      $t(
                        'ibe.checkout.address.changesApplied',
                        'The following changes were applied:'
                      )
                    }}
                  </p>
                  <ul
                    v-if="
                      preSubmitValidation.addressCorrectionAlertData.value.addressChanges.length > 0
                    "
                    class="mt-1 list-disc list-inside text-base"
                  >
                    <li
                      v-for="change in preSubmitValidation.addressCorrectionAlertData.value
                        .addressChanges"
                      :key="change.field"
                    >
                      {{ $t(change.fieldLabelKey, change.fieldLabelFallback) }}: "{{
                        change.originalValue
                      }}" → "{{ change.correctedValue }}"
                    </li>
                  </ul>

                  <!-- Please verify message -->
                  <p class="mt-2">
                    {{
                      $t(
                        'ibe.checkout.address.pleaseVerify',
                        'Please verify the changes and continue.'
                      )
                    }}
                  </p>

                  <!-- Flow change notice (if 734) -->
                  <p
                    v-if="
                      preSubmitValidation.addressCorrectionAlertData.value.showFlowChangedNotice
                    "
                    class="mt-2 font-medium"
                  >
                    {{
                      $t(
                        'ibe.checkout.flowChangedToRequest',
                        'Your booking will be processed as a request.'
                      )
                    }}
                  </p>
                </BaseAlert>

                <!-- Auxiliary Unavailable Alert (merged BA rejected selected extras/insurance) -->
                <BaseAlert
                  v-if="preSubmitValidation.auxErrorMessage.value"
                  type="error"
                  :message="preSubmitValidation.auxErrorMessage.value"
                  class="mt-4"
                />

                <!-- Validation Error Alert (generic — network, unrecoverable) -->
                <BaseAlert
                  v-if="preSubmitValidation.validationError.value"
                  type="error"
                  :message="preSubmitValidation.validationError.value"
                  class="mt-4"
                />
              </template>

              <template v-else-if="checkout.activeStep === 2">
                <!-- Flow change notice (booking type switched on submit, user must resubmit) -->
                <BaseAlert
                  v-if="checkout.bookingResult?.flowChanged"
                  type="info"
                  :message="
                    $t(
                      'ibe.checkout.booking.flowChanged',
                      'The reservation type has changed. Please review the details and submit again.'
                    )
                  "
                  class="mt-4 mb-6"
                />

                <!-- Heading + intro copy: the only two things that differ per flow -->
                <template v-if="checkout.isRequestSubmitCta">
                  <BaseHeadline
                    :title="$t('ibe.pay.onrequest.headline', 'Order is available on request')"
                    container-margin="mb-5"
                    heading-level="h3"
                    heading-class="text-text-primary"
                    wrapper-class=""
                  />
                  <div class="mb-8 text-base text-text-secondary">
                    <p>
                      {{
                        $t(
                          'ibe.pay.onrequest.message',
                          'The selected offer is available on request. Our team will check the availability and get back to you as soon as possible.'
                        )
                      }}
                    </p>
                  </div>
                </template>
                <template v-else>
                  <BaseHeadline
                    :title="
                      $t('ibe.checkout.payment-options.title', 'Review and select payment option')
                    "
                    container-margin="mb-5"
                    heading-level="h3"
                    heading-class="text-text-primary"
                    wrapper-class=""
                  />
                  <div class="mb-8 text-base text-text-secondary">
                    <p class="mb-5">
                      {{
                        $t(
                          'ibe.checkout.payment-options.label',
                          'On the right, you will find your travel and booking details—please review them and select your payment option.'
                        )
                      }}
                    </p>
                    <p>
                      {{
                        $t(
                          'ibe.checkout.payment-options.description',
                          'You will receive the payment information with your booking confirmation by e-mail.'
                        )
                      }}
                    </p>
                  </div>
                </template>

                <!-- TODO: Voucher code — will be implemented in a future iteration -->
                <!-- <CheckoutVoucher
                  :title="$t('ibe.checkout.discount')"
                  :text="$t('ibe.checkout.discount-text')"
                  :button-text="$t('ibe.checkout.discount-button')"
                /> -->

                <CheckoutPaymentSchedule
                  :total-price="totalPrice"
                  :schedule="checkout.paymentSchedule"
                  :price-before-rebates="
                    checkout.hasDiscount ? totalPrice - checkout.totalDiscount : undefined
                  "
                  :rebates="checkout.rebateSummary"
                />

                <CheckoutPaymentMethod v-if="checkout.isPaymentMethodVisible" />

                <CheckboxList
                  list-class="flex flex-col"
                  :model-value="terms.values"
                  :title="$t('ibe.checkout.conditions')"
                  :options="terms.checkboxOptions.value"
                  @checkbox-change="terms.handleChange"
                >
                  <!-- Statutory: package travel directive form + pre-contractual info (visible iff FORM and/or VVI link present) -->
                  <template #termsStatutory>
                    <span v-html="terms.statutoryLabelHtml.value"></span>
                  </template>

                  <!-- Contractual: GTC + cancellation + (optional) insurance terms -->
                  <template #termsContractual>
                    <span v-html="terms.contractualLabelHtml.value"></span>
                  </template>

                  <!-- Privacy Policy -->
                  <template #privacyPolicy>
                    <I18nHtml
                      key-name="ibe.terms.privacy-policy"
                      fallback="I have read and understood the <a href='/datenschutzhinweise' target='_blank'>privacy policy</a>."
                    />
                  </template>
                </CheckboxList>
              </template>
            </div>
          </div>

          <CheckoutFooter
            v-if="checkout.activeStep < 3"
            :active-step="checkout.activeStep"
            :total-price="totalPrice"
            :button-text="checkoutButtonText"
            :disabled="checkout.isSubmittingBooking || preSubmitValidation.isValidating.value"
            :loading="checkout.isSubmittingBooking || preSubmitValidation.isValidating.value"
            :hotel-name="summaryProps.hotelName"
            :hotel-image-url="summaryProps.hotelImageUrl"
            :date-range="summaryProps.dateRange"
            :pax-label="summaryProps.paxLabel"
            :schedule="checkout.paymentSchedule"
            class="w-full px-2"
            @next-step="handleNextStep"
            @show-details="handleShowDetails"
          />
        </div>

        <!-- Desktop: summary sidebar (mobile uses the collapsed card above) -->
        <CheckoutSummaryColumn
          v-if="checkout.activeStep < 3"
          ref="desktopSummaryRef"
          v-bind="summaryProps"
          class="hidden lg:block lg:w-1/3"
          @edit-room="handleChangeRoomSelection"
          @edit-services="() => checkout.setActiveStep(0)"
          @edit-contact="() => checkout.setActiveStep(1)"
        />
      </div>
    </div>
  </div>
</template>
